import type { JSX } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  LuBuilding2,
  LuClipboardList,
  LuInbox,
  LuLogOut,
  LuSettings,
  LuUsers,
} from 'react-icons/lu';
import useFetchAdminIam from '../api/useFetchAdminIam';

type SidebarItem = {
  label: string;
  tab?: 'assistant_todos' | 'organizations' | 'users' | 'import_requests' | 'settings';
  icon: JSX.Element;
};

const mainItems: SidebarItem[] = [
  {
    label: 'アシスタントTODO',
    tab: 'assistant_todos',
    icon: <LuClipboardList className="size-5 text-[#4188FF]" />,
  },
  {
    label: 'クライアント管理',
    tab: 'organizations',
    icon: <LuBuilding2 className="size-5 text-[#4188FF]" />,
  },
  {
    label: 'ユーザー管理',
    tab: 'users',
    icon: <LuUsers className="size-5 text-[#4188FF]" />,
  },
  {
    label: '求人クロールリクエスト',
    tab: 'import_requests',
    icon: <LuInbox className="size-5 text-[#4188FF]" />,
  },
];

const footerItems: SidebarItem[] = [
  {
    label: '設定',
    tab: 'settings',
    icon: <LuSettings className="size-5" />,
  },
];

export default function AdminSidebar() {
  const { data: currentUser } = useFetchAdminIam();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canAccessOrganizations = isSuperAdmin || currentUser?.role === 'admin';
  const { location } = useRouterState();
  const currentTab =
    (location.search as { tab?: SidebarItem['tab'] } | undefined)?.tab ?? 'assistant_todos';
  const isActive = (tab?: SidebarItem['tab']) => tab === currentTab;
  const visibleMainItems = mainItems.filter((item) => {
    if (item.tab === 'users') return isSuperAdmin;
    if (item.tab === 'organizations') return canAccessOrganizations;
    return true;
  });

  return (
    <aside className="flex h-screen w-[250px] flex-col bg-[#f3f7fe] shadow-[2px_0px_2px_0px_rgba(94,135,203,0.25)]">
      <div className="flex h-[72px] w-full items-center justify-center border-0 border-b border-solid border-[#c4d0e4]">
        <img src="/admin/logo.png" alt="エージェントハブ Admin" className="h-10 object-contain" />
      </div>
      <div className="flex flex-1 flex-col justify-between px-3 pt-3 pb-6">
        <nav className="flex flex-col gap-2">
          {visibleMainItems.map((item) => (
            <Link
              key={item.label}
              to="/admin"
              search={{ tab: item.tab }}
              className={clsx(
                'flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm font-bold text-[#40567a] no-underline',
                isActive(item.tab) ? 'bg-[#dae8ff]' : 'hover:bg-[#e8f0ff]'
              )}
            >
              <span className="flex items-center justify-center">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="flex flex-col gap-2">
          {footerItems.map((item) => (
            <Link
              key={item.label}
              to="/admin"
              search={{ tab: item.tab }}
              className={clsx(
                'flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm font-bold text-[#40567a] no-underline',
                isActive(item.tab) ? 'bg-[#dae8ff]' : 'hover:bg-[#e8f0ff]'
              )}
            >
              <span className="flex items-center justify-center">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
          <form action="/admin/sign_out" method="post">
            <input type="hidden" name="_method" value="delete" />
            <input
              type="hidden"
              name="authenticity_token"
              value={
                (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ||
                ''
              }
            />
            <button
              type="submit"
              className="flex h-8 w-full cursor-pointer items-center gap-2 rounded border-none bg-transparent px-2 text-left text-sm font-bold text-[#40567a] hover:bg-[#e8f0ff]"
            >
              <span className="flex items-center justify-center">
                <LuLogOut className="size-5" />
              </span>
              <span>ログアウト</span>
            </button>
          </form>
        </div>
      </div>
    </aside>
  );
}
